import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Editor, EditorTools } from "@progress/kendo-react-editor";
import { toast } from "react-toastify";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "@progress/kendo-theme-default/dist/all.css";
import useAuthStore from "../../../store/authStore";
import useDocumentTitle from "../../../shared/customHooks/useDocumentTitle";
import {
  copyAndSaveEmailTemplate,
  deleteEmailTemplate,
  downloadEmailAttachmentContentInfo,
  getEmailTemplateFields,
  getEmailTemplateList,
  getEmailTemplateTypes,
  getTemplateAttachments,
  insertOrUpdateEmailTemplate,
  insertTemplateAttachments,
  testSendEmailTemplate,
  updateDefaultTemplate,
} from "../services/adminEmail_service";

const {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  FontName,
  FontSize,
  FormatBlock,
  AlignLeft,
  OrderedList,
  UnorderedList,
  Indent,
  Outdent,
  Link,
  Unlink,
  InsertImage,
  Undo,
  Redo,
  ViewHtml,
} = EditorTools;

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary);
};

const fileExtension = (fileName) => {
  if (!fileName) return "";
  const idx = fileName.lastIndexOf(".");
  return idx >= 0 ? fileName.slice(idx + 1) : "";
};

const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const XButton = ({ title, onClick }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    style={{
      border: "none",
      border: "1px solid rgb(169, 68, 66)",
      background: "rgb(217, 83, 79)",
      padding: "0px 4px 6px 0px",
      color: "rgb(255, 255, 255)",
      fontWeight: 700,
      fontSize: "26px",
      width: 20,
      height: 20,
      lineHeight: "11px",
      textAlign: "center",
      borderRadius: 2,
      cursor: "pointer",
    }}
  >
    ×
  </button>
);

const modalBackdropStyle = {
  display: "block",
  backgroundColor: "rgba(0,0,0,0.4)",
};

const spinnerStyle = {
  width: 44,
  height: 44,
  border: "4px solid #d9e2ef",
  borderTopColor: "#337ab7",
  borderRadius: "50%",
  animation: "templates-tab-spin 0.9s linear infinite",
};

const ConfirmModal = ({
  show,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmClassName = "btn btn-primary",
  onConfirm,
  onClose,
  busy = false,
}) => {
  if (!show) return null;

  return (
    <div className="modal fade in" role="dialog" aria-modal="true" style={modalBackdropStyle} onClick={onClose}>
      <div className="modal-dialog" style={{ width: 520 }} role="document" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header" style={{ padding: "0 15px" }}>
            <button type="button" className="close" title="Close" onClick={onClose} disabled={busy}>
              &times;
            </button>
            <h4 className="modal-title" style={{ margin: "12px 0" }}>
              {title}
            </h4>
          </div>
          <div className="modal-body">
            <p style={{ marginBottom: 0 }}>{message}</p>
          </div>
          <div className="modal-footer">
            <button type="button" className={confirmClassName} onClick={onConfirm} disabled={busy}>
              {busy ? "Working..." : confirmText}
            </button>
            <button type="button" className="btn btn-default" onClick={onClose} disabled={busy}>
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PromptModal = ({
  show,
  title,
  readOnlyLabel,
  readOnlyValue,
  label,
  value,
  onChange,
  confirmText = "Save",
  cancelText = "Cancel",
  onConfirm,
  onClose,
  busy = false,
}) => {
  if (!show) return null;

  return (
    <div className="modal fade in" role="dialog" aria-modal="true" style={modalBackdropStyle} onClick={onClose}>
      <div className="modal-dialog" style={{ width: 560 }} role="document" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header" style={{ padding: "0 15px" }}>
            <button type="button" className="close" title="Close" onClick={onClose} disabled={busy}>
              &times;
            </button>
            <h4 className="modal-title" style={{ margin: "12px 0" }}>
              {title}
            </h4>
          </div>
          <div className="modal-body">
            {readOnlyLabel ? (
              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label style={{ width: "32%", marginBottom: 0, lineHeight: "34px" }}>{readOnlyLabel}</label>
                <div style={{ width: "68%", display: "inline-block", paddingLeft:"10px" }}>
                  <input type="text" className="form-control" value={readOnlyValue || ""} disabled />
                </div>
              </div>
            ) : null}
            <div className="form-group" style={{ marginBottom: "5px" }}>
              <label style={{ width: "32%", marginBottom: 0, lineHeight: "34px" }}>{label}</label>
              <div style={{ width: "68%", display: "inline-block" , paddingLeft:"10px"}}>
                <input
                  type="text"
                  className="form-control"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  disabled={busy}
                  autoFocus
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={busy}>
              {busy ? "Working..." : confirmText}
            </button>
            <button type="button" className="btn btn-default" onClick={onClose} disabled={busy}>
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const toPreviewHtml = (html) =>
  String(html || "")
    .replace(/&lt;&lt;/g, "<<")
    .replace(/&gt;&gt;/g, ">>");

const getAny = (obj, ...keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const normalizeTemplateType = (t) => ({
  templateTypeId: getAny(t, "templateTypeId", "templateTypeID"),
  templateType: getAny(t, "templateType", "templateTypeName"),
});

const normalizeTemplateField = (f) => ({
  fieldId: getAny(f, "fieldId"),
  fieldTypeId: getAny(f, "fieldTypeId"),
  typeName: getAny(f, "typeName"),
  fieldName: getAny(f, "fieldName"),
  fieldTag: getAny(f, "fieldTag"),
});

const normalizeTemplate = (t) => ({
  templateId: getAny(t, "templateId"),
  templateTypeID: getAny(t, "templateTypeID","templateTypeId"),
  templateTypeName: getAny(t, "templateTypeName"),
  templateName: getAny(t, "templateName"),
  description: getAny(t, "description"),
  fromEmailAddress: getAny(t, "fromEmailAddress"),
  cc: getAny(t,"cc"),
  bcc: getAny(t, "bcc"),
  emailTemplate: getAny(t, "emailTemplate"),
  emailSubject: getAny(t, "emailSubject"),
  attachmentsDetails: getAny(t, "attachmentsDetails"),
  userId: getAny(t, "userId"),
  isDefault: getAny(t, "isDefault"),
  createdDate: getAny(t, "createdDate"),
  createdBy: getAny(t, "createdBy"),
  modifiedDate: getAny(t, "modifiedDate"),
  modifiedBy: getAny(t, "modifiedBy"),
  deleteAttachmentIds: getAny(t,  "deleteAttachmentIds"),
});

const normalizeAttachment = (a) => ({
  attachmentId: getAny(a, "attachmentId"),
  attachmentType: getAny(a, "attachmentType"),
  templateId: getAny(a, "templateId"),
  fileContent: getAny(a, "fileContent"),
  name: getAny(a, "name"),
  fileChecksum: getAny(a, "fileChecksum"),
  fileName: getAny(a, "fileName", "name"),
  extension: getAny(a, "extension"),
  fileSize: getAny(a, "fileSize"),
  mode: getAny(a, "mode"),
});

export default function TemplatesTab({ isActive = true }) {
  useDocumentTitle("Email Template");

  const userId = useAuthStore((s) => s.user);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const editorTools = useMemo(() => {
    const groups = [
      [Bold, Italic, Underline, Strikethrough],
      [Subscript, Superscript],
      [FontName, FontSize, FormatBlock],
      [AlignLeft],
      [OrderedList, UnorderedList, Indent, Outdent],
      [Link, Unlink, InsertImage],
      [Undo, Redo],
      [ViewHtml],
    ];

    return groups.map((g) => g.filter(Boolean)).filter((g) => g.length > 0);
  }, []);

  const [templates, setTemplates] = useState([]);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [templateFields, setTemplateFields] = useState([]);

  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [pageSize] = useState(15);

  const [templateTypeId, setTemplateTypeId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [description, setDescription] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [body, setBody] = useState("<p></p>");

  const [attachments, setAttachments] = useState([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState([]);
  const [newFiles, setNewFiles] = useState([]);

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyTemplateName, setCopyTemplateName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [testSendModalOpen, setTestSendModalOpen] = useState(false);

  const fileInputRef = useRef(null);

  const normalizeList = (response) => {
    const raw = response?.data?.data ?? response?.data ?? response;
    return Array.isArray(raw) ? raw : raw ? [raw] : [];
  };

  const loadMeta = useCallback(async () => {
    try {
      const [typesRes, fieldsRes] = await Promise.all([getEmailTemplateTypes(), getEmailTemplateFields()]);
      setTemplateTypes(normalizeList(typesRes).map(normalizeTemplateType));
      setTemplateFields(normalizeList(fieldsRes).map(normalizeTemplateField));
    } catch {
      // ignore
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setGridLoading(true);
    setError("");
    try {
      const res = await getEmailTemplateList();
      const list = normalizeList(res).map(normalizeTemplate);
      setTemplates(list);
      return list;
    } catch {
      setTemplates([]);
      setError("Failed to load email templates.");
      return [];
    } finally {
      setLoading(false);
      setGridLoading(false);
    }
  }, []);

  const loadAttachments = useCallback(
    async (id) => {
      if (!id) return setAttachments([]);
      try {
        const res = await getTemplateAttachments(1, 0, id);
        setAttachments(normalizeList(res).map(normalizeAttachment));
      } catch {
        setAttachments([]);
      }
    },
    []
  );

  const bindForm = useCallback(
    async (item) => {
      setSelected(item);
      setTemplateTypeId(String(item?.templateTypeID ?? item?.templateTypeId ?? ""));
      setTemplateName(item?.templateName ?? "");
      setDescription(item?.description ?? "");
      setFromEmail(item?.fromEmailAddress ?? "");
      setCc(item?.cc ?? "");
      setBcc(item?.bcc ?? "");
      setSubject(item?.emailSubject ?? "");
      setIsDefault(item?.isDefault);
      setBody(item?.emailTemplate ?? "<p></p>");
      setDeletedAttachmentIds([]);
      setNewFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await loadAttachments(item?.templateId);
    },
    [loadAttachments]
  );

  useEffect(() => {
    if (!isActive) return;
    loadMeta();
    loadTemplates().then((list) => {
      if (list.length > 0) bindForm(list[0]);
    });
  }, [isActive, loadMeta, loadTemplates, bindForm]);

  const onAddNew = () => {
    setSelected({ templateId: 0 });
    setTemplateTypeId("");
    setTemplateName("");
    setDescription("");
    setFromEmail("");
    setCc("");
    setBcc("");
    setSubject("");
    setIsDefault(false);
    setBody("<p></p>");
    setAttachments([]);
    setDeletedAttachmentIds([]);
    setNewFiles([]);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onCopyTemplate = async () => {
    if (!selected?.templateId) {
      setError("Please select a template to copy.");
      toast.error("Please select a template to copy.");
      return;
    }

    setCopyTemplateName(`${selected.templateName || "Template"} - Copy`);
    setCopyModalOpen(true);
  };

  const confirmCopyTemplate = async () => {
    const trimmedName = copyTemplateName.trim();
    if (!trimmedName) {
      setError("Please provide a name for the copied template.");
      toast.error("Please provide a name for the copied template.");
      return;
    }

    setActionBusy(true);
    setError("");
    try {
      const res = await copyAndSaveEmailTemplate({
        sourceTemplateId: selected.templateId,
        templateName: trimmedName,
        userId: Number(userId) || 0,
      });
      const result = res?.data ?? res;
      if (Number(result) === -1) {
        setError("Template already exists.");
        toast.error("Template already exists.");
        return;
      }
      if (Number(result) <= 0) {
        setError("Failed to copy template.");
        toast.error("Failed to copy template.");
        return;
      }

      const list = await loadTemplates();
      const created = list.find((t) => Number(t.templateId) === Number(result)) || null;
      if (created) await bindForm(created);
      setCopyModalOpen(false);
      toast.success("Template copied successfully.");
    } catch {
      setError("Failed to copy template.");
      toast.error("Failed to copy template.");
    } finally {
      setActionBusy(false);
    }
  };

  const onSetDefault = async (row) => {
    setActionBusy(true);
    setError("");
    try {
      await updateDefaultTemplate({
        templateId: row.templateId,
        templateTypeID: row.templateTypeID,
        modifiedBy: Number(userId) || 0,
      });
      const list = await loadTemplates();
      const refreshed = list.find((t) => Number(t.templateId) === Number(row.templateId)) || null;
      if (refreshed) await bindForm(refreshed);
      toast.success("Default template updated.");
    } catch {
      setError("Failed to update default template.");
      toast.error("Failed to update default template.");
    } finally {
      setActionBusy(false);
    }
  };

  const onDeleteTemplate = async (row) => {
    setDeleteTarget(row);
  };

  const confirmDeleteTemplate = async () => {
    if (!deleteTarget?.templateId) return;

    setActionBusy(true);
    setError("");
    try {
      const res = await deleteEmailTemplate({ templateId: deleteTarget.templateId });
      const result = res?.data ?? res;
      if (Number(result) <= 0) {
        setError("Failed to delete email template.");
        toast.error("Failed to delete email template.");
        return;
      }
      const list = await loadTemplates();
      if (selected?.templateId === deleteTarget.templateId) {
        if (list.length > 0) await bindForm(list[0]);
        else onAddNew();
      }
      setDeleteTarget(null);
      toast.success("Template deleted successfully.");
    } catch {
      setError("Failed to delete email template.");
      toast.error("Failed to delete email template.");
    } finally {
      setActionBusy(false);
    }
  };

  const onSave = async () => {
    setError("");

    if (!templateTypeId || Number(templateTypeId) <= 0) {
      setError("Please provide the template type.");
      toast.error("Please provide the template type.");
      return;
    }
    if (!templateName) {
      setError("Please provide the template name.");
      toast.error("Please provide the template name.");
      return;
    }
    if (!subject) {
      setError("Please provide the template subject.");
      toast.error("Please provide the template subject.");
      return;
    }
    if (!fromEmail) {
      setError("Please provide the template from details.");
      toast.error("Please provide the template from details.");
      return;
    }
    if (!body || body === "<p></p>") {
      setError("Please provide the email template body details.");
      toast.error("Please provide the email template body details.");
      return;
    }

    setActionBusy(true);
    try {
      const dto = {
        templateId: Number(selected?.templateId) || 0,
        templateTypeID: Number(templateTypeId),
        templateName: templateName,
        description: description,
        fromEmailAddress: fromEmail,
        cc: cc,
        bcc: bcc,
        emailSubject: subject,
        emailTemplate: body,
        isDefault: isDefault,
        createdBy: Number(userId) || 0,
        modifiedBy: Number(userId) || 0,
        deleteAttachmentIds: deletedAttachmentIds.join(","),
      };

      const saveRes = await insertOrUpdateEmailTemplate(dto);
      const savedTemplateId = Number(saveRes?.data ?? saveRes) || 0;
      if (!savedTemplateId) {
        setError("Failed to save template.");
        toast.error("Failed to save template.");
        return;
      }

      if (newFiles.length > 0) {
        const attachmentItems = await Promise.all(
          newFiles.map(async (f) => {
            const buffer = await f.arrayBuffer();
            return {
              attachmentId: 0,
              fileName: f.name,
              extension: fileExtension(f.name),
              fileSize: Number(f.size) || 0,
              fileContent: arrayBufferToBase64(buffer),
            };
          })
        );

        await insertTemplateAttachments({
          templateId: savedTemplateId,
          userId: Number(userId) || 0,
          attachmentsDetails: attachmentItems,
        });
      }

      const list = await loadTemplates();
      const refreshed = list.find((t) => Number(t.templateId) === Number(savedTemplateId)) || null;
      if (refreshed) await bindForm(refreshed);
      toast.success(Number(selected?.templateId) > 0 ? "Template updated successfully." : "Template saved successfully.");
    } catch {
      setError("Failed to save template.");
      toast.error("Failed to save template.");
    } finally {
      setActionBusy(false);
    }
  };

  const onPreview = () => {
    setIsPreviewOpen(true);
  };

  const onTestSend = async () => {
    if (!selected?.templateId || Number(selected.templateId) <= 0) {
      setError('Please "Save/Update" the template before using Test Send.');
      toast.error('Please "Save/Update" the template before using Test Send.');
      return;
    }

    setTestSendModalOpen(true);
  };

  const confirmTestSend = async () => {
    setActionBusy(true);
    setError("");
    try {
      await testSendEmailTemplate({
        templateId: Number(selected.templateId),
        templateTypeID: Number(templateTypeId) || 0,
        templateName: templateName,
        description: description,
        fromEmailAddress: fromEmail,
        cc: cc,
        bcc: bcc,
        emailSubject: subject,
        emailTemplate: body,
        userId: Number(userId) || 0,
        modifiedBy: Number(userId) || 0,
      });
      setTestSendModalOpen(false);
      toast.success("Test email sent successfully.");
    } catch {
      setError("Failed to send test email.");
      toast.error("Failed to send test email.");
    } finally {
      setActionBusy(false);
    }
  };

  const insertTag = (tag) => {
    if (!tag) return;
    const escaped = String(tag).replace(/</g, "&lt;").replace(/>/g, "&gt;");
    setBody((prev) => `${prev || ""}${escaped}`);
  };

  const removeAttachment = useCallback((att) => {
    if (!att?.attachmentId) return;
    setDeletedAttachmentIds((prev) => (prev.includes(att.attachmentId) ? prev : [...prev, att.attachmentId]));
    setAttachments((prev) => prev.filter((a) => a.attachmentId !== att.attachmentId));
  }, []);

  const downloadAttachment = useCallback(async (att) => {
    if (!att?.attachmentId) return;
    setError("");
    try {
      const res = await downloadEmailAttachmentContentInfo(att.attachmentId);
      const blob = res?.data ?? res;
      downloadBlob(blob, att.fileName || att.name || "attachment");
    } catch {
      setError("Failed to download attachment.");
      toast.error("Failed to download attachment.");
    }
  }, []);

  const columnDefs = useMemo(
    () => [
      { field: "templateId", hide: true },
      { field: "templateTypeID", hide: true },
      { field: "fromEmailAddress", hide: true },
      { field: "cc", hide: true },
      { field: "bcc", hide: true },
      { field: "emailSubject", hide: true },
      { field: "emailTemplate", hide: true },
      { field: "isDefault", hide: true },
      { field: "templateName", headerName: "Name", minWidth: 180, flex: 0.9, sortable: true, filter: "agTextColumnFilter" },
      { field: "description", headerName: "Description", minWidth: 220, flex: 1, sortable: true, filter: "agTextColumnFilter" },
      { field: "templateTypeName", headerName: "Template Type", minWidth: 180, flex: 0.9, sortable: true, filter: "agTextColumnFilter" },
      {
        headerName: "Default",
        minWidth: 120,
        maxWidth: 120,
        sortable: false,
        filter: false,
        cellStyle: { display: "flex", justifyContent: "center", alignItems: "center" },
        cellRenderer: (p) => {
          const row = p.data;
          const checked = row?.isDefault;
          return (
            <input
              type="checkbox"
              checked={checked}
              disabled={checked}
              onChange={() => onSetDefault(row)}
              title={checked ? "Default" : "Set Default"}
            />
          );
        },
      },
      {
        headerName: "",
        minWidth: 110,
        maxWidth: 110,
        sortable: false,
        filter: false,
        cellStyle: { display: "flex", justifyContent: "center", alignItems: "center" },
        cellRenderer: (p) => <XButton title="Delete Template" onClick={() => onDeleteTemplate(p.data)} />,
      },
    ],
    [onDeleteTemplate]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      floatingFilter: true,
    }),
    []
  );

  const attachmentDefaultColDef = useMemo(
    () => ({
      resizable: true,
      floatingFilter: false,
    }),
    []
  );

  const attachmentColumnDefs = useMemo(
    () => [
      { field: "attachmentId", hide: true },
      { field: "templateId", hide: true },
      { field: "attachmentType", hide: true },
      { field: "extension", hide: true },
      { field: "fileSize", hide: true },
      {
        headerName: "File Name",
        minWidth: 260,
        flex: 1,
        sortable: true,
        filter: false,
        cellRenderer: (p) => (
          <div style={{display:"flex", justifyContent:"left"}}>
            <button
              type="button"
              style={{ border: "none", background: "transparent", padding: 0, color: "#337ab7"}}
              onClick={() => downloadAttachment(p.data)}
              title="Download"
            >
              {p.data.fileName}
            </button>
          </div>
        ),
      },
      {
        headerName: "",
        minWidth: 70,
        maxWidth: 70,
        sortable: false,
        filter: false,
        cellStyle: { display: "flex", justifyContent: "center", alignItems: "center" },
        cellRenderer: (p) => <XButton title="Remove" onClick={() => removeAttachment(p.data)} />,
      },
    ],
    [downloadAttachment, removeAttachment]
  );

  const activeAttachmentList = useMemo(() => attachments || [], [attachments]);

  return (
    <div style={{ paddingTop: 10 }}>
      <style>
        {`
          @keyframes templates-tab-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="row" style={{ marginBottom: 10 }}>
        <div className="col-sm-12 text-right">
          <button type="button" className="btn btn-primary" onClick={onCopyTemplate} style={{ marginRight: 8 }}>
            Copy Template
          </button>
          <button type="button" className="btn btn-primary" onClick={onAddNew}>
            Add New
          </button>
        </div>
      </div>

      {error ? <div style={{ color: "red", marginBottom: 12 }}>{error}</div> : null}

      <div className="ag-theme-alpine" style={{ width: "100%", height: "520px", position: "relative" }}>
        <AgGridReact
          theme={"legacy"}
          rowData={templates}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onRowClicked={(e) => bindForm(e.data)}
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[10, 15, 50, 100, 200]}
          suppressCellFocus={true}
          overlayNoRowsTemplate={loading ? "Loading..." : "No Records Available"}
          getRowId={(p) => String(p.data?.templateId )}
        />
        {gridLoading ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.75)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 12,
              zIndex: 2,
            }}
          >
            <div style={spinnerStyle} />
            <div style={{ color: "#245580", fontWeight: 600 }}>Loading templates...</div>
          </div>
        ) : null}
      </div>

      <div style={{ height: 18 }} />

      <div style={{ border: "2px solid #0000FF", padding: 12 }}>
        <div className="row" style={{ marginBottom: 10 }}>
          <div className="col-sm-12 text-right">
            <button type="button" className="btn btn-primary" onClick={onTestSend} style={{ marginRight: 8 }}>
              Test Send
            </button>
            <button type="button" className="btn btn-primary" onClick={onPreview} style={{ marginRight: 8 }}>
              Preview
            </button>
            <button type="button" className="btn btn-primary" onClick={onSave} disabled={loading || actionBusy}>
              {Number(selected?.templateId) > 0 ? "Update" : "Save"}
            </button>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-5" style={{ paddingRight: 0 }}>
            <div className="form-group">
              <label style={{ width: "19%" }}>Template Type:</label>
              <div style={{ width: "81%", display: "inline-block" }}>
                <select
                  className="form-control"
                  value={templateTypeId}
                  onChange={(e) => setTemplateTypeId(e.target.value)}
                >
                  <option value=""> -- Select --</option>
                  {templateTypes.map((t) => (
                    <option key={t.templateTypeId} value={t.templateTypeId}>
                      {t.templateType}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label style={{ width: "19%" }}>Name:</label>
              <div style={{ width: "81%", display: "inline-block" }}>
                <input
                  type="text"
                  className="form-control"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label style={{ width: "19%" }}>Description:</label>
              <div style={{ width: "81%", display: "inline-block" }}>
                <input
                  type="text"
                  className="form-control"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label style={{ width: "19%" }}>From:</label>
              <div style={{ width: "81%", display: "inline-block" }}>
                <input type="text" className="form-control" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label style={{ width: "19%" }}>CC:</label>
              <div style={{ width: "81%", display: "inline-block" }}>
                <input type="text" className="form-control" value={cc} onChange={(e) => setCc(e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label style={{ width: "19%" }}>BCC:</label>
              <div style={{ width: "81%", display: "inline-block" }}>
                <input type="text" className="form-control" value={bcc} onChange={(e) => setBcc(e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label style={{ width: "19%" }}>Subject:</label>
              <div style={{ width: "81%", display: "inline-block" }}>
                <input type="text" className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label style={{ width: "19%" }}>Default:</label>
              <div style={{ width: "81%", display: "inline-block", textAlign: "left" }}>
                <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
              </div>
            </div>

            <div className="form-group">
              <label style={{ width: "19%" }}>Tags:</label>
              <div style={{ width: "81%", display: "inline-block" }}>
                <select
                  className="form-control"
                  value=""
                  onChange={(e) => {
                    insertTag(e.target.value);
                    e.target.value = "";
                  }}
                >
                  <option value=""> -- Select --</option>
                  {templateFields.map((f) => (
                    <option key={f.fieldId} value={f.fieldTag}>
                      {f.fieldName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label style={{ width: "19%" }}>Attachments:</label>
              <div style={{ width: "81%", display: "inline-block" }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={(e) => setNewFiles(Array.from(e.target.files || []))}
                />
              </div>
            </div>

            <div className="form-group">
              <label style={{ width: "19%" }}>Files:</label>
              <div style={{ width: "81%", display: "inline-block" }}>
                <div className="ag-theme-alpine" style={{ width: "100%", height: 250 }}>
                  <AgGridReact
                    theme={"legacy"}
                    rowData={activeAttachmentList}
                    columnDefs={attachmentColumnDefs}
                    defaultColDef={attachmentDefaultColDef}
                    pagination={true}
                    paginationPageSize={3}
                    paginationPageSizeSelector={[3, 10, 15, 50, 100, 200]}
                    suppressCellFocus={true}
                    overlayNoRowsTemplate={"No Records Available"}
                    getRowId={(p) => String(p.data?.attachmentId )}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-7" style={{ paddingLeft: 12 }}>
            <Editor
              value={body}
              onChange={(e) => setBody(e.html)}
              tools={editorTools}
              style={{ height: 380 }}
            />
          </div>
        </div>
      </div>

      {isPreviewOpen ? (
        <div
          className="modal fade in"
          role="dialog"
          aria-modal="true"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)" }}
          onClick={() => setIsPreviewOpen(false)}
        >
          <div
            className="modal-dialog"
            style={{ width: "60%" }}
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header" style={{ background: "#0b0b64", color: "#fff" }}>
                <button
                  type="button"
                  className="close"
                  title="Close"
                  onClick={() => setIsPreviewOpen(false)}
                  style={{ color: "#fff", opacity: 1 }}
                >
                  ×
                </button>
                <h4 className="modal-title">Mail Content Preview</h4>
              </div>

              <div className="modal-body" style={{ overflow: "auto", height: 600 }}>
                <div className="row" style={{ marginBottom: 8 }}>
                  <div className="col-sm-8">
                    <h5 style={{ fontWeight: "bold", marginTop: 0 }}>
                      &lt;FirstName&gt; &lt;LastName&gt; , &lt;Email&gt;
                    </h5>
                  </div>
                  <div className="col-sm-4 text-right">
                    <label className="control-label" style={{ marginBottom: 0 }}>
                      0 of 0 Recipients
                    </label>
                  </div>
                </div>

                <hr style={{ borderTop: "1px solid #2C81BA", marginTop: 7 }} />

                <div
                  style={{ padding: 4 }}
                  dangerouslySetInnerHTML={{ __html: toPreviewHtml(body) }}
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-default" onClick={() => setIsPreviewOpen(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <PromptModal
        show={copyModalOpen}
        title="Copy Email Template"
        readOnlyLabel="Template copied"
        readOnlyValue={selected?.templateName || selected?.TemplateName || ""}
        label="New template name"
        value={copyTemplateName}
        onChange={setCopyTemplateName}
        confirmText="Save"
        onConfirm={confirmCopyTemplate}
        onClose={() => {
          if (actionBusy) return;
          setCopyModalOpen(false);
        }}
        busy={actionBusy}
      />

      <ConfirmModal
        show={deleteTarget}
        title="Delete Template"
        message={`The template "${deleteTarget?.templateName || ""}" will be deleted permanently. Continue?`}
        confirmText="Delete"
        confirmClassName="btn btn-danger"
        onConfirm={confirmDeleteTemplate}
        onClose={() => {
          if (actionBusy) return;
          setDeleteTarget(null);
        }}
        busy={actionBusy}
      />

      <ConfirmModal
        show={testSendModalOpen}
        title="Send Test Email"
        message="A test email will be sent to your email address. Continue?"
        confirmText="Send"
        onConfirm={confirmTestSend}
        onClose={() => {
          if (actionBusy) return;
          setTestSendModalOpen(false);
        }}
        busy={actionBusy}
      />
    </div>
  );
}
